import { useEffect, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '../../ui/card';
import { Tabs, TabsList, TabsTrigger } from '../../ui/tabs';
import { Loader2, Truck, Eye, Plus, Sparkles, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { purchasingAPI, coreAPI, AdvancedShippingNotice, CommodityGroup, CommodityType, Site, CompanyGroup } from '../../../lib/api';
import { CreateASNModal, ASNDetailModal } from '../ASNModals';
import { Pagination } from '../../ui/pagination';
import { SectionTourTrigger } from '../../tour/SectionTourTrigger';
import { useAuth } from '../../../contexts/AuthContext';
import { useFormatters } from '../../../contexts/PreferencesContext';
import { ASNFilterSection } from './ASNFilterSection';

// CR-3420: Sortable columns configuration
type SortDirection = 'asc' | 'desc' | null;
interface SortConfig {
  column: string;
  direction: SortDirection;
}

export default function ASNTab() {
  const { user } = useAuth();
  // ASN creation is supplier-only (suppliers create ASNs to notify customers of shipments)
  const isSupplier = user?.platform === 'SUPPLIER';

  const [asns, setAsns] = useState<AdvancedShippingNotice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [showFilters, setShowFilters] = useState(false);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedAsn, setSelectedAsn] = useState<AdvancedShippingNotice | null>(null);
  const [createInitialCallOffId, setCreateInitialCallOffId] = useState<number | null>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const [pageSize, setPageSize] = useState(50);
  const [searchParams, setSearchParams] = useSearchParams();

  // Auto-open Create ASN wizard when navigated via /purchasing?tab=asn&action=create&call_off=<id>
  useEffect(() => {
    if (!isSupplier) return;
    if (searchParams.get('action') !== 'create') return;
    const callOffParam = searchParams.get('call_off');
    setCreateInitialCallOffId(callOffParam ? Number(callOffParam) : null);
    setShowCreateModal(true);
    // Clear the trigger params so reloads/back-navigations don't reopen the modal.
    const next = new URLSearchParams(searchParams);
    next.delete('action');
    next.delete('call_off');
    setSearchParams(next, { replace: true });
  }, [searchParams, isSupplier, setSearchParams]);

  // CR-3420: Sort state
  const [sortConfig, setSortConfig] = useState<SortConfig>({ column: 'created_at', direction: 'desc' });

  // Text input states (for debouncing)
  const [textInputs, setTextInputs] = useState({
    asnNumber: '',
    supplier: '',
    trackingNumber: '',
    poNumber: '',
    poLineNumber: '',
  });

  // Actual filter state (used for API calls)
  const [filters, setFilters] = useState({
    asnNumber: '',
    supplier: '',
    trackingNumber: '',
    poNumber: '',
    poLineNumber: '',
    status: '',
    courier: '',
    courierDeliveryStatus: '',
    dateFrom: '',
    dateTo: '',
    site: '',
    supplierGroup: '',
    commodityGroup: '',
    commodityType: '',
    strategic: '',
  });

  // Filter dropdown options
  const [siteOptions, setSiteOptions] = useState<{ value: string; label: string }[]>([]);
  const [supplierGroupOptions, setSupplierGroupOptions] = useState<{ value: string; label: string }[]>([]);
  const [commodityGroupOptions, setCommodityGroupOptions] = useState<{ value: string; label: string }[]>([]);
  const [commodityTypeOptions, setCommodityTypeOptions] = useState<{ value: string; label: string }[]>([]);

  // CR-4580: No filter persistence — always start fresh
  useEffect(() => {
    setInitialLoadComplete(true);
    // Fetch filter options
    Promise.all([
      coreAPI.getAccessibleSites().catch(() => ({ data: [] })),
      purchasingAPI.getCommodityGroups({ enabled: true }).catch(() => ({ data: [] })),
      purchasingAPI.getCommodityTypes({ enabled: true }).catch(() => ({ data: [] })),
      coreAPI.getCompanyGroups({ page_size: 200 }).catch(() => ({ data: { results: [] } })),
    ]).then(([sitesRes, groupsRes, typesRes, companyGroupsRes]) => {
      const sites = (Array.isArray(sitesRes.data) ? sitesRes.data : (sitesRes.data as unknown as { results: Site[] }).results || []) as Site[];
      setSiteOptions(sites.map((s) => ({ value: String(s.id), label: s.name })));

      const groups = Array.isArray(groupsRes.data) ? groupsRes.data : (groupsRes.data as unknown as { results: CommodityGroup[] }).results || [];
      setCommodityGroupOptions(groups.filter((g: CommodityGroup) => g.enabled).map((g: CommodityGroup) => ({ value: String(g.id), label: g.name })));

      const types = Array.isArray(typesRes.data) ? typesRes.data : (typesRes.data as unknown as { results: CommodityType[] }).results || [];
      setCommodityTypeOptions(types.filter((t: CommodityType) => t.enabled).map((t: CommodityType) => ({ value: String(t.id), label: t.name })));

      const companyGroups = (companyGroupsRes.data as unknown as { results: CompanyGroup[] }).results || [];
      setSupplierGroupOptions(companyGroups.map((g: CompanyGroup) => ({ value: String(g.id), label: g.name })));
    });
  }, []);

  // Debounce text inputs
  useEffect(() => {
    if (!initialLoadComplete) return;

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      setFilters(prev => ({
        ...prev,
        asnNumber: textInputs.asnNumber,
        supplier: textInputs.supplier,
        trackingNumber: textInputs.trackingNumber,
        poNumber: textInputs.poNumber,
        poLineNumber: textInputs.poLineNumber,
      }));
    }, 500);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [textInputs, initialLoadComplete]);

  useEffect(() => {
    if (initialLoadComplete) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, filters, activeTab, initialLoadComplete, sortConfig, pageSize]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      // CR-3420: Build ordering parameter from sortConfig
      const ordering = sortConfig.direction === 'asc'
        ? sortConfig.column
        : `-${sortConfig.column}`;

      const queryParams: Record<string, string | number | boolean> = {
        ordering,
        page: currentPage,
        page_size: pageSize,
      };

      // Individual field filters using Django filter syntax
      if (filters.asnNumber) {
        queryParams['asn_number__icontains'] = filters.asnNumber;
      }

      if (filters.supplier) {
        queryParams['supplier__name__icontains'] = filters.supplier;
      }

      if (filters.trackingNumber) {
        queryParams['tracking_number__icontains'] = filters.trackingNumber;
      }

      if (filters.courier) {
        queryParams['courier'] = filters.courier;
      }

      // Apply status filter from dropdown or active tab
      if (filters.status) {
        queryParams['status'] = filters.status;
      } else if (activeTab !== 'all') {
        queryParams['status'] = activeTab;
      }

      // Apply date filters
      if (filters.dateFrom) {
        queryParams['expected_delivery_date__gte'] = filters.dateFrom;
      }
      if (filters.dateTo) {
        queryParams['expected_delivery_date__lte'] = filters.dateTo;
      }

      // New filter params
      if (filters.poNumber) {
        queryParams['schedules__po_line__po__po_number__icontains'] = filters.poNumber;
      }
      if (filters.poLineNumber) {
        queryParams['schedules__po_line__line_number'] = filters.poLineNumber;
      }
      if (filters.site) {
        queryParams['schedules__po_line__po__customer'] = filters.site;
      }
      if (filters.supplierGroup) {
        queryParams['supplier__group'] = filters.supplierGroup;
      }
      if (filters.commodityGroup) {
        queryParams['schedules__po_line__commodity_group'] = filters.commodityGroup;
      }
      if (filters.commodityType) {
        queryParams['schedules__po_line__commodity_type'] = filters.commodityType;
      }

      if (filters.courierDeliveryStatus) {
        queryParams['courier_delivery_status'] = filters.courierDeliveryStatus;
      }

      if (filters.strategic === 'yes') {
        queryParams['supplier__supplier_profile__is_preferred'] = true;
      }

      const response = await purchasingAPI.getASNs(queryParams);

      setAsns(response.data.results);
      setTotalCount(response.data.count);
      setTotalPages(Math.ceil(response.data.count / pageSize));
    } catch (err: unknown) {
      console.error('Error fetching ASN data:', err);
      const axiosErr = err as { response?: { data?: { detail?: string } } };
      setError(axiosErr.response?.data?.detail || 'Failed to load ASN data');
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setTextInputs({
      asnNumber: '',
      supplier: '',
      trackingNumber: '',
      poNumber: '',
      poLineNumber: '',
    });
    setFilters({
      asnNumber: '',
      supplier: '',
      trackingNumber: '',
      poNumber: '',
      poLineNumber: '',
      status: '',
      courier: '',
      courierDeliveryStatus: '',
      dateFrom: '',
      dateTo: '',
      site: '',
      supplierGroup: '',
      commodityGroup: '',
      commodityType: '',
      strategic: '',
    });
    setCurrentPage(1);
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setCurrentPage(1);
  };

  const handleFilterChange = (key: string, value: string) => {
    // Text inputs: update textInputs state (will be debounced)
    if (['asnNumber', 'supplier', 'trackingNumber', 'poNumber', 'poLineNumber'].includes(key)) {
      setTextInputs({ ...textInputs, [key]: value });
    } else if (key === 'commodityGroup') {
      // Cascade: reset commodity type when group changes
      setFilters({ ...filters, commodityGroup: value, commodityType: '' });
    } else {
      // Dropdowns and dates: update filters directly (instant)
      setFilters({ ...filters, [key]: value });
    }
    setCurrentPage(1);
  };

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    setCurrentPage(1);
  };

  // CR-3420: Handle column sorting
  const handleSort = (column: string) => {
    setSortConfig(prev => {
      if (prev.column === column) {
        // Toggle direction: asc -> desc -> asc
        return {
          column,
          direction: prev.direction === 'asc' ? 'desc' : 'asc',
        };
      }
      // New column, default to descending
      return { column, direction: 'desc' };
    });
    setCurrentPage(1);
  };

  // CR-3420: Sortable column header component
  const SortableHeader = ({
    column,
    label,
    className = '',
    children,
  }: {
    column: string;
    label: string;
    className?: string;
    children?: React.ReactNode;
  }) => {
    const isActive = sortConfig.column === column;
    return (
      <th
        className={`text-left py-3 px-4 font-medium cursor-pointer hover:bg-muted/50 select-none transition-colors ${className}`}
        onClick={() => handleSort(column)}
      >
        <div className="flex items-center gap-1">
          {children || label}
          <span className="text-muted-foreground">
            {isActive ? (
              sortConfig.direction === 'asc' ? (
                <ArrowUp className="h-3.5 w-3.5" />
              ) : (
                <ArrowDown className="h-3.5 w-3.5" />
              )
            ) : (
              <ArrowUpDown className="h-3.5 w-3.5 opacity-30" />
            )}
          </span>
        </div>
      </th>
    );
  };

  const handleCreateASN = async (data: Record<string, string | number | boolean> | { asn_number: string; schedules: number[]; courier: string; tracking_number: string; method_of_transport: string; weight_kg: string; distance_km: string; expected_delivery_date: string; notes: string }) => {
    try {
      await purchasingAPI.createASN(data as Partial<AdvancedShippingNotice>);
      await fetchData();
    } catch (error: unknown) {
      console.error('Failed to create ASN:', error);
      const axiosErr = error as { response?: { data?: { message?: string } } };
      alert(axiosErr.response?.data?.message || 'Failed to create ASN');
      throw error;
    }
  };

  const handleViewDetails = (asn: AdvancedShippingNotice) => {
    setSelectedAsn(asn);
    setShowDetailModal(true);
  };

  const { formatDate: contextFormatDate, formatDateTime: contextFormatDateTime } = useFormatters();

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    return contextFormatDate(dateString);
  };

  // CR-3858: Format date with time for created_at column
  const formatDateTime = (dateString: string) => {
    if (!dateString) return '-';
    return contextFormatDateTime(dateString);
  };

  const getStatusBadge = (status: string) => {
    const statusMap: Record<string, { label: string; class: string }> = {
      created: { label: 'Created', class: 'bg-gray-100 text-gray-800' },
      submitted: { label: 'Submitted', class: 'bg-blue-100 text-blue-800' },
      in_transit: { label: 'In Transit', class: 'bg-yellow-100 text-yellow-800' },
      delivered: { label: 'Delivered', class: 'bg-green-100 text-green-800' },
      received: { label: 'Received', class: 'bg-green-100 text-green-800' },
      cancelled: { label: 'Cancelled', class: 'bg-red-100 text-red-800' },
    };
    const config = statusMap[status] || { label: status, class: 'bg-gray-100 text-gray-800' };
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.class}`}>
        {config.label}
      </span>
    );
  };

  if (loading && asns.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
          <p className="mt-2 text-sm text-muted-foreground">Loading ASNs...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800">Error: {error}</p>
            <button
              onClick={fetchData}
              className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
            >
              Try again
            </button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Section Tour Trigger */}
      <div className="flex justify-end">
        <SectionTourTrigger
          pageKey="purchasing"
          sectionKey="asn"
          variant="link"
          label="Tour this tab"
        />
      </div>

      {/* Filter Section */}
      {showFilters && (
        <ASNFilterSection
          filters={filters}
          textInputs={textInputs}
          siteOptions={siteOptions}
          supplierGroupOptions={supplierGroupOptions}
          commodityGroupOptions={commodityGroupOptions}
          commodityTypeOptions={commodityTypeOptions}
          onFilterChange={handleFilterChange}
          onClearFilters={clearFilters}
          onSearch={() => fetchData()}
          onHideFilters={() => setShowFilters(false)}
        />
      )}

      {/* Header with Tabs */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Truck className="h-5 w-5 text-blue-600" />
              <h3 className="font-semibold">Advanced Shipping Notices</h3>
              <span className="text-sm text-muted-foreground">
                {totalCount} {totalCount === 1 ? 'ASN' : 'ASNs'}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${
                  showFilters ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'
                }`}
              >
                {showFilters ? 'Hide' : 'Show'} Filters
              </button>
              {/* Create ASN button - only visible to suppliers (they create ASNs to notify customers of shipments) */}
              {isSupplier && (
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-1"
                  data-tour="create-asn-btn"
                >
                  <Plus className="h-4 w-4" />
                  Create ASN
                </button>
              )}
            </div>
          </div>

          <Tabs value={activeTab} onValueChange={handleTabChange}>
            <TabsList className="grid w-full grid-cols-6" data-tour="asn-status-tabs">
              <TabsTrigger value="all">All</TabsTrigger>
              <TabsTrigger value="created">Created</TabsTrigger>
              <TabsTrigger value="submitted">Submitted</TabsTrigger>
              <TabsTrigger value="in_transit">In Transit</TabsTrigger>
              <TabsTrigger value="delivered">Delivered</TabsTrigger>
              <TabsTrigger value="received">Received</TabsTrigger>
            </TabsList>
          </Tabs>
        </CardContent>
      </Card>

      {/* ASN Table */}
      <Card data-tour="asn-table">
        <CardContent className="p-0">
          {asns.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              No ASNs found matching your criteria.
            </div>
          ) : (
            <>
              {/* Top Pagination */}
              <div className="px-4">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalCount={totalCount}
                  itemsPerPage={pageSize}
                  onPageChange={handlePageChange}
                  onPageSizeChange={handlePageSizeChange}
                  itemLabel="ASNs"
                />
              </div>

              {/* CR-3866: Added max-height and sticky header support */}
              <div className="overflow-x-auto overflow-y-auto max-h-[calc(100vh-350px)]">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 z-20">
                    <tr className="border-b bg-muted/80 backdrop-blur-sm">
                      <th className="text-left py-3 px-4 font-medium sticky left-0 bg-muted/30 z-10 min-w-[100px]">View</th>
                      <SortableHeader column="asn_number" label="ASN Number" className="min-w-[140px]" />
                      <SortableHeader column="status" label="Status" className="min-w-[140px]" />
                      <SortableHeader column="supplier__name" label="Supplier" className="min-w-[180px]" />
                      <SortableHeader column="customer__name" label="Customer" className="min-w-[180px]" />
                      <th className="text-left py-3 px-4 font-medium min-w-[180px]">Company</th>
                      <SortableHeader column="courier" label="Courier" className="min-w-[120px]" />
                      <SortableHeader column="tracking_number" label="Tracking Number" className="min-w-[160px]" />
                      {/* CR-3415: Premium Feature - Courier Status (not sortable) */}
                      <th className="text-left py-3 px-4 font-medium min-w-[160px]">
                        <div className="flex items-center gap-1">
                          Courier Status
                          <span className="px-1.5 py-0.5 text-[10px] bg-gradient-to-r from-amber-400 to-orange-500 text-white rounded-full font-bold flex items-center gap-0.5" title="Premium Feature - Real-time tracking via Courier API">
                            <Sparkles className="h-2.5 w-2.5" />
                            PRO
                          </span>
                        </div>
                      </th>
                      <SortableHeader column="method_of_transport" label="Transport Method" className="min-w-[140px]" />
                      <SortableHeader column="weight_kg" label="Weight (kg)" className="min-w-[110px] text-right" />
                      <SortableHeader column="distance_km" label="Distance (km)" className="min-w-[120px] text-right" />
                      <SortableHeader column="carbon_emission_kg" label="Carbon (kg CO₂)" className="min-w-[130px] text-right" />
                      {/* CR-3415: Premium Feature - Expected Delivery with live updates */}
                      <SortableHeader column="expected_delivery_date" label="Expected Delivery" className="min-w-[160px]">
                        <span>Expected Delivery</span>
                        <span className="px-1.5 py-0.5 text-[10px] bg-gradient-to-r from-amber-400 to-orange-500 text-white rounded-full font-bold flex items-center gap-0.5 ml-1" title="Premium Feature - Auto-updated via Courier API">
                          <Sparkles className="h-2.5 w-2.5" />
                          PRO
                        </span>
                      </SortableHeader>
                      <SortableHeader column="actual_delivery_date" label="Actual Delivery" className="min-w-[140px]" />
                      {/* CR-3858: Created date column */}
                      <SortableHeader column="created_at" label="Created" className="min-w-[140px]" />
                    </tr>
                  </thead>
                  <tbody>
                    {asns.map((asn: AdvancedShippingNotice) => (
                      <tr
                        key={asn.id}
                        className="border-b hover:bg-muted/50 transition-colors"
                      >
                        <td className="py-3 px-4 sticky left-0 bg-background z-10">
                          <button
                            onClick={() => handleViewDetails(asn)}
                            className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 flex items-center gap-1"
                          >
                            <Eye className="h-3.5 w-3.5" />
                            View
                          </button>
                        </td>
                        <td className="py-3 px-4 font-medium">{asn.asn_number}</td>
                        <td className="py-3 px-4">{getStatusBadge(asn.status)}</td>
                        <td className="py-3 px-4">{asn.supplier_details?.name || '-'}</td>
                        <td className="py-3 px-4">{asn.customer_name || asn.customer_details?.name || '-'}</td>
                        <td className="py-3 px-4">{asn.customer_details?.name || '-'}</td>
                        <td className="py-3 px-4 uppercase">{asn.courier || '-'}</td>
                        <td className="py-3 px-4 font-mono">{asn.tracking_number || '-'}</td>
                        {/* CR-3415: Courier Status - Premium Feature */}
                        <td className="py-3 px-4">
                          {asn.tracking_number && asn.courier ? (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-500 border border-dashed border-gray-300">
                              <Sparkles className="h-3 w-3 text-amber-500" />
                              Enable PRO
                            </span>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </td>
                        <td className="py-3 px-4 capitalize">{asn.method_of_transport || '-'}</td>
                        <td className="py-3 px-4 text-right">{asn.weight_kg || '-'}</td>
                        <td className="py-3 px-4 text-right">{asn.distance_km || '-'}</td>
                        <td className="py-3 px-4 text-right font-medium text-green-700">
                          {asn.carbon_emission_kg || asn.calculated_carbon_emission || '-'}
                        </td>
                        <td className="py-3 px-4">{formatDate(asn.estimated_delivery_date || '')}</td>
                        <td className="py-3 px-4">{formatDate(asn.actual_delivery_date || '')}</td>
                        {/* CR-3858: Created date and time cell */}
                        <td className="py-3 px-4">{formatDateTime(asn.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Bottom Pagination */}
              <div className="px-4">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalCount={totalCount}
                  itemsPerPage={pageSize}
                  onPageChange={handlePageChange}
                  onPageSizeChange={handlePageSizeChange}
                  itemLabel="ASNs"
                />
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {loading && (
        <div className="flex items-center justify-center p-4">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      )}

      {/* Create ASN Modal */}
      <CreateASNModal
        isOpen={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          setCreateInitialCallOffId(null);
        }}
        onSubmit={handleCreateASN}
        initialCallOffId={createInitialCallOffId ?? undefined}
      />

      {/* ASN Detail Modal */}
      {showDetailModal && selectedAsn && (
        <ASNDetailModal
          isOpen={showDetailModal}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedAsn(null);
          }}
          asn={selectedAsn}
          onSubmit={async () => await fetchData()}
        />
      )}
    </div>
  );
}
